import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { InventoryProjection, InventoryProjectionLine, Part } from 'app/core/model/entity-model';
import { PartDetailComponent } from 'app/parts/part-detail/part-detail.component';
import { PartSearchComponent } from 'app/parts/part-search/part-search.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-inventory-projection-detail',
    templateUrl: './inventory-projection-detail.component.html',
    styleUrls: ['./inventory-projection-detail.component.scss'],
    standalone: false,
})
export class InventoryProjectionDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    projection: InventoryProjection;
    id: any;
    lines: InventoryProjectionLine[];

    createPO = false;
    vendors: any[];
    vendorId: number;

    readOnly = false;
    processing = false;
    showAsDialog = false;

    isToastVisible = false;
    validationGroup = 'mainValidationGroup';
    errorAction = 'saving';

    saving = false;
    includeAssemblyParts = false;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private utility: UtilityService,
    ) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }
    hasChanges() {
        return this.uow.hasChanges();
    }

    async ngOnInit() {
        if (!this.id) this.id = this.route.snapshot.params.id;
        await this.getData();
    }

    async getData() {
        if (this.id === 'new') {
            this.projection = this.uow.inventoryProjections.createEntity() as InventoryProjection;
            this.projection.number = (await this.utility.getNextInventoryProjectionNumber()) as any;
            this.projection.created = new Date();
            this.projection.createdBy = this.auth.currentUser.userName;
        } else {
            this.projection = (await this.uow.inventoryProjections.byId(this.id, 'lines.parts')) as InventoryProjection;
        }
        this.getDataLines();
    }

    getDataLines() {
        this.lines = this.projection.lines;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    onSelectionChange(e) {}

    async onRowUpdating(e) {}

    async onRowDoubleClick(e) {
        if (!e.data || !e.data.id) return;

        const orderId = this.projection.id;
        if (!orderId) return;
        const modalRef = this.modalService.show(PartDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partId: e.data.partId,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
            }
            modalRef.hide();
        });
    }

    async removeLine() {
        if (this.linesGrid.instance.getSelectedRowsData().length === 0 || this.processing) return;
        this.appInfo.pleaseWaitShow();
        const selectedLine = this.linesGrid.instance.getSelectedRowsData()[0];
        selectedLine.entityAspect.setDeleted();
        await this.uow.commit();
        this.getDataLines();
        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async addLines(mode: any) {
        let partTypeIds = [];
        let partOptions = [];
        const line = this.linesGrid.instance.getSelectedRowsData()[0];

        partTypeIds = (await this.uow.partTypes.all()).map((partType: any) => partType.id);

        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partTypeIds: partTypeIds,
                partOptions: partOptions,
                mode: mode,
                columnLimitMode: true,
                showSideBar: true,
                storageKey: 'part-search-grid-projection-detail',
                projectionsMode: true,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.saving = true;
                const parts = (await this.uow.parts.byIds(result.partIds)) as Part[];
                result.partIds.forEach((partId) => {
                    const part = parts.filter((part) => part.id === partId)[0];
                    this.add(this.projection, part, 1);
                });
                await this.save();
                this.getDataLines();
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    add(detail?: any, part?: any, quantity?: number) {
        const line = this.uow.inventoryProjectionLines.createEntity() as InventoryProjectionLine;
        line.inventoryProjectionId = detail.id;
        const lineNumber = this.lines.length > 0 ? (_.maxBy(this.lines, 'lineNumber') as any).lineNumber + 1 : 1;
        line.lineNumber = lineNumber;
        if (part) {
            line.partId = part.id;
            line.partNumber = part.partNumber;
            line.description = part.description;
        }
        line.quantity = quantity;
        return line;
    }

    async submit() {
        const success = await Promise.resolve(this.save());
        let pONumber = '0';
        if (!success) return;

        const negativeCheck =
            this.lines.filter((x) => {
                if (x.quantity < 0 && !x.notes) return x;
            }).length > 0;

        if (!!negativeCheck) {
            await alert(`Negative lines require a note indicating which built date they are negating.`, `${this.appInfo.settings.company}`);
            return;
        }

        // if (!this.projection.createPO) {
        //     const success = await confirm('Are you sure you want to submit this Build Assembly without creating a PO?', this.appInfo.settings.company);
        //     if (!success) return;
        // }

        // if (this.projection.createPO) {
        //     const result = await confirm(
        //         `Build Assembly is marked to create PO.  This will generate a PO and build details will be incremented when PO is received.`,
        //         `${this.appInfo.settings.company}`
        //     );
        //     if (!result) return;

        //     this.appInfo.pleaseWaitShow();
        //     let purchaseOrder = this.uow.purchaseOrders.createEntity() as PurchaseOrder;
        //     purchaseOrder.reportGuid = crypto.randomUUID();
        //     purchaseOrder.statusId = 1;
        //     purchaseOrder.pONumber = (await this.utility.getNextOrderNumber()) as any;
        //     pONumber = purchaseOrder.pONumber;
        //     purchaseOrder.createdById = this.auth.currentUser.id;
        //     purchaseOrder.createDate = new Date();
        //     purchaseOrder.pODate = new Date();
        //     purchaseOrder.vendorId = this.projection.vendorId;
        //     purchaseOrder.note = this.projection.notes;
        //     this.projection.purchaseOrderId = purchaseOrder.id;
        //     // purchaseOrder.shipToId = this.buildAssembly.shipToId;
        //     await this.uow.commit();

        //     for (let index = 0; index < this.lines.length; index++) {
        //         const detail = this.lines[index];
        //         // console.log(detail);
        //         if (this.projection.createPO) {
        //             const aliases = await this.uow.getAliasByPartId(detail.partId);
        //             await this.addPurchaseOrderLine(detail.part, detail.quantity, purchaseOrder, aliases, detail);
        //         }
        //     }

        //     await this.uow.commit();
        //     this.appInfo.pleaseWaitHide();
        // }

        this.appInfo.pleaseWaitShow();
        // let detailIds = [];
        // this.lines.forEach((detail) => {
        //     detail.created = new Date();
        //     detail.status = 1;
        //     detailIds.push(detail.id);
        // });
        // await this.uow.commit();

        // this.projection.statusId = 2;
        await this.save();
        (await this.utility.getNextInventoryProjectionNumber()) as any;
        // call to utility to process the order line parts
        await this.utility.processInventoryProjection(this.projection.id, this.includeAssemblyParts);
        this.appInfo.pleaseWaitHide();

        // if (this.projection.createPO) {
        //     await alert(`Purchase Order#: ${pONumber} created`, `${this.appInfo.settings.company}`);
        // }
    }

    async save() {
        const success = await Promise.resolve(this.validate());
        if (!success) return false;
        this.isToastVisible = true;
        await this.uow.commit();
        if (this.id === 'new') {
            this.id = this.projection.id;
            this.router.navigate(['/projections/' + this.projection.id]);
        }
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.router.navigate(['/projections/overview']);
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }
    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }
}
